import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtMoney, fmtMonth, fmtNumber } from '../lib/format';
import type { ProfitView } from '../lib/types';
import { t } from '../i18n';
import { Empty, ErrorState, Ltr } from './ui';
import { MarginBadge, profitTitle } from './Margin';
import { useOpenDocument } from './Layout';
import { OptimizePanel } from './Optimize';

type P = ProfitView & { invoices: number };
type ProductRow = P & { productId: number; sku: string; name: string };
type CustomerProfitT = {
  target: number;
  warn: number;
  total: P;
  last12: P;
  months: Array<P & { month: string }>;
  invoices: Array<P & { documentId: number; number: string | null; type: keyof typeof t.enums.docType | null; date: string }>;
  strong: ProductRow[];
  weak: ProductRow[];
};

/** Customer file → "רווחיות": 12 months, the trend, every invoice with its margin, strong and weak products. */
export function CustomerProfit({ customerId }: { customerId: number }) {
  const open = useOpenDocument();
  const { data, error, refetch } = useQuery({ queryKey: ['profit', 'customer', customerId], queryFn: () => api<CustomerProfitT>(`/api/profit/customers/${customerId}`) });
  if (error) return <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (!data) return <div className="panel panel-body"><span className="spinner" /></div>;
  if (!data.invoices.length) return <div className="panel"><Empty title="אין עדיין חשבוניות ללקוח" body="הרווחיות מחושבת מהחשבוניות שיצאו ללקוח (כולל הסניפים)." /></div>;
  const maxProfit = Math.max(1, ...data.months.map((m) => Math.abs(m.profit)));
  const first = data.months.find((m) => m.margin != null);
  const last = [...data.months].reverse().find((m) => m.margin != null);
  const trend = first && last && first !== last ? Math.round((last.margin! - first.margin!) * 10) / 10 : null;

  return (
    <div className="stack">
      <OptimizePanel customerId={customerId} />
      <section className="kpis">
        <div className="kpi" title={profitTitle(data.last12)}>
          <div className="kpi-label">רווחיות — 12 חודשים</div>
          <div className="kpi-value"><MarginBadge margin={data.last12.margin} level={data.last12.level} title={profitTitle(data.last12)} /></div>
          <div className="kpi-meta">
            יעד {data.target}%
            {trend != null ? (
              <span style={{ color: trend >= 0 ? 'var(--ok)' : 'var(--bad)' }}> · <bdi dir="ltr">{trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}</bdi> נק׳ מתחילת התקופה</span>
            ) : null}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">רווח גולמי — 12 חודשים</div>
          <div className="kpi-value">{fmtMoney(data.last12.profit, { round: true })}</div>
          <div className="kpi-meta">{fmtNumber(data.last12.invoices)} חשבוניות · כיסוי {data.last12.coverage}%</div>
        </div>
        <div className="kpi" title={profitTitle(data.total)}>
          <div className="kpi-label">מאז ומעולם</div>
          <div className="kpi-value"><MarginBadge margin={data.total.margin} level={data.total.level} title={profitTitle(data.total)} /></div>
          <div className="kpi-meta">רווח {fmtMoney(data.total.profit, { round: true })} · {fmtNumber(data.total.invoices)} חשבוניות</div>
        </div>
      </section>

      <div className="grid-main-side wide-side">
        <section className="panel">
          <div className="panel-head"><h2>חשבוניות</h2></div>
          <div className="table-wrap" style={{ maxHeight: 520, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>מסמך</th>
                  <th>תאריך</th>
                  <th className="num">סכום</th>
                  <th className="num">רווח</th>
                  <th className="num">רווחיות</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((d) => (
                  <tr key={d.documentId} className="clickable" onClick={() => open(d.documentId)}>
                    <td>{d.type ? t.enums.docType[d.type] : ''} <Ltr className="tabular strong">{d.number ?? '—'}</Ltr></td>
                    <td className="tabular">{fmtDate(d.date)}</td>
                    <td className="num tabular">{fmtMoney(d.revenue)}</td>
                    <td className="num tabular">{d.margin == null ? <span className="faint">—</span> : fmtMoney(d.profit)}</td>
                    <td className="num">
                      <MarginBadge margin={d.margin} level={d.level} title={profitTitle(d)} />
                      {d.coverage < 60 && d.margin != null ? <span className="sub faint">{d.coverage}% מהסכום</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head"><h2>לפי חודשים</h2></div>
          <table className="table">
            <tbody>
              {[...data.months].reverse().map((m) => (
                <tr key={m.month} title={profitTitle(m)}>
                  <td>
                    <div className="nowrap">{fmtMonth(m.month)}</div>
                    <div className="bar-track" style={{ marginTop: 4, width: `${Math.max(3, (Math.abs(m.profit) / maxProfit) * 100)}%` }}>
                      <div className="paid" style={{ width: '100%' }} />
                    </div>
                  </td>
                  <td className="num tabular">{m.margin == null ? '—' : fmtMoney(m.profit, { round: true })}</td>
                  <td className="num"><MarginBadge margin={m.margin} level={m.level} title={profitTitle(m)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <div className="grid-2">
        <ProductList title="מוצרים חזקים — הכי הרבה רווח" rows={data.strong} empty="אין עדיין מוצרים עם עלות" />
        <ProductList title={`מוצרים חלשים — מתחת ליעד (${data.target}%)`} rows={data.weak} empty="כל המוצרים ביעד" />
      </div>
    </div>
  );
}

function ProductList({ title, rows, empty }: { title: string; rows: ProductRow[]; empty: string }) {
  return (
    <section className="panel">
      <div className="panel-head"><h2>{title}</h2><span className="spacer" /><span className="small muted">12 חודשים</span></div>
      {rows.length === 0 ? (
        <div className="panel-body small muted">{empty}</div>
      ) : (
        <table className="table">
          <tbody>
            {rows.map((p) => (
              <tr key={p.productId} title={profitTitle(p)}>
                <td className="primary">{p.name}<span className="sub"><Ltr>{p.sku}</Ltr></span></td>
                <td className="num tabular">{fmtMoney(p.profit, { round: true })}<span className="sub">מתוך {fmtMoney(p.covered, { round: true })}</span></td>
                <td className="num"><MarginBadge margin={p.margin} level={p.level} title={profitTitle(p)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
